import { useEffect, useMemo, useState } from 'react';
import { FolderKanban, Link2, Plus, RefreshCcw, Search, Send, Users } from 'lucide-react';
import { api, LoiMayChu } from '../services/api';
import type { BoSuuTapNhom, Nhom, TaiKhoanFB } from '../services/kieu';
import { AnhDaiDien, ChuaCoDuLieu, DangTai, DauThe, NutChinh, NutPhu, The } from '../components/ui';
import { tenActor } from '../services/taiKhoan';

type Tab = 'nhom' | 'bo-suu-tap' | 'tham-gia';

const JOIN_LIMIT = 5;

function parseInput(raw: string): string[] {
  return [...new Set(raw.split(/[\n,\s]+/).map(x => {
    const t = x.trim();
    const m = t.match(/(?:facebook\.com\/)?groups\/([0-9a-zA-Z._-]+)/i);
    return (m ? m[1] : t).replace(/[?#].*$/, '').trim();
  }).filter(Boolean))];
}

export function ManKenhNhom({ taiKhoan }: { taiKhoan: TaiKhoanFB | null }) {
  const [tab, setTab] = useState<Tab>('nhom');
  const [nhom, setNhom] = useState<Nhom[]>([]);
  const [collections, setCollections] = useState<BoSuuTapNhom[]>([]);
  const [dangTai, setDangTai] = useState(true);
  const [loi, setLoi] = useState<string | null>(null);
  const [tuKhoa, setTuKhoa] = useState('');
  const [collectionDangChon, setCollectionDangChon] = useState('ALL');
  const [daChon, setDaChon] = useState<Set<string>>(new Set());
  const [tenBoMoi, setTenBoMoi] = useState('');
  const [boGan, setBoGan] = useState('');
  const [joinInput, setJoinInput] = useState('');
  const [joinCandidates, setJoinCandidates] = useState<Nhom[]>([]);
  const [joinSelected, setJoinSelected] = useState<Set<string>>(new Set());
  const [joinMsg, setJoinMsg] = useState<string | null>(null);
  const [dangGuiJoin, setDangGuiJoin] = useState(false);

  async function taiDuLieu() {
    try {
      setDangTai(true);
      const [g, c] = await Promise.all([api.nhom(taiKhoan?.uid), api.boSuuTapNhom(taiKhoan?.uid)]);
      setNhom(g.groups || []);
      setCollections(c.collections || []);
      setBoGan(c.collections?.[0]?.name || '');
      setLoi(g.reason ? (g.message || g.reason) : null);
    } catch (err) {
      setLoi(err instanceof LoiMayChu ? err.message : 'Không tải được dữ liệu nhóm.');
    } finally {
      setDangTai(false);
    }
  }

  useEffect(() => {
    setDaChon(new Set());
    setJoinSelected(new Set());
    setJoinCandidates([]);
    taiDuLieu();
  }, [taiKhoan?.uid]);

  const nhomLoc = useMemo(() => {
    const q = tuKhoa.trim().toLowerCase();
    return nhom.filter(g => {
      if (collectionDangChon === 'UNCLASSIFIED' && g.collection_name) return false;
      if (collectionDangChon !== 'ALL' && collectionDangChon !== 'UNCLASSIFIED' && g.collection_name !== collectionDangChon) return false;
      if (!q) return true;
      return String(g.name || '').toLowerCase().includes(q) || String(g.id || '').includes(q);
    });
  }, [nhom, tuKhoa, collectionDangChon]);

  async function taoBoSuuTap() {
    if (!tenBoMoi.trim()) return;
    try {
      await api.taoBoSuuTapNhom(tenBoMoi.trim());
      setTenBoMoi('');
      await taiDuLieu();
    } catch (err) {
      setLoi(err instanceof LoiMayChu ? err.message : 'Không tạo được bộ sưu tập.');
    }
  }

  async function ganBoSuuTap() {
    if (!taiKhoan?.uid || daChon.size === 0 || !boGan) return;
    try {
      await api.ganNhomVaoBoSuuTap(taiKhoan.uid, [...daChon], boGan);
      setDaChon(new Set());
      await taiDuLieu();
    } catch (err) {
      setLoi(err instanceof LoiMayChu ? err.message : 'Không gán được bộ sưu tập.');
    }
  }

  async function timUngVienJoin() {
    const ids = parseInput(joinInput);
    if (ids.length > 0 && ids.every(x => /^[0-9a-zA-Z._-]+$/.test(x)) && !joinInput.trim().includes(' ')) {
      const candidates = ids.slice(0, JOIN_LIMIT).map(id => ({ id, name: `Nhóm ${id}`, avatar: '', collection_name: '', url: `https://www.facebook.com/groups/${id}`, source: 'INPUT' }));
      setJoinCandidates(candidates);
      setJoinSelected(new Set(candidates.map(g => g.id)));
      setJoinMsg(ids.length > JOIN_LIMIT ? `Chỉ lấy ${JOIN_LIMIT} nhóm đầu tiên theo giới hạn thí điểm.` : null);
      return;
    }
    try {
      const res = await api.timNhom(joinInput.trim(), 20);
      setJoinCandidates(res.groups || []);
      setJoinSelected(new Set());
      setJoinMsg(res.message || null);
    } catch (err) {
      setJoinMsg(err instanceof LoiMayChu ? err.message : 'Không tìm được nhóm.');
    }
  }

  async function guiJoin() {
    const ids = [...joinSelected].slice(0, JOIN_LIMIT);
    if (ids.length === 0) return;
    try {
      setDangGuiJoin(true);
      const key = `JOIN_GROUPS:${taiKhoan?.uid || 'unknown'}:${ids.sort().join('-')}:${crypto.randomUUID()}`;
      const res = await api.thamGiaNhom(ids, key);
      setJoinMsg(res.message || 'Đã tiếp nhận yêu cầu tham gia nhóm.');
      setJoinSelected(new Set());
    } catch (err) {
      setJoinMsg(err instanceof LoiMayChu ? err.message : 'Không gửi được yêu cầu tham gia nhóm.');
    } finally {
      setDangGuiJoin(false);
    }
  }

  return (
    <div className="mx-auto max-w-[1440px] animate-fade-in space-y-5">
      <The className="p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <AnhDaiDien src={taiKhoan?.avatar} ten={tenActor(taiKhoan)} kichThuoc="h-14 w-14" />
            <div className="min-w-0">
              <h2 className="truncate text-lg font-bold text-slate-900 dark:text-white">Kênh & Nhóm</h2>
              <p className="truncate text-sm text-slate-500 dark:text-slate-400">Actor: {tenActor(taiKhoan)} · {taiKhoan?.uid || 'chưa chọn'} · {taiKhoan?.isPage ? 'Page' : 'Cá nhân'}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <NutPhu onClick={taiDuLieu}><RefreshCcw className="h-3.5 w-3.5" />Tải lại</NutPhu>
            <NutPhu onClick={() => api.dongBoNhom().then(taiDuLieu).catch(e => setLoi(e instanceof LoiMayChu ? e.message : 'Không đồng bộ được nhóm.'))}>Đồng bộ lại</NutPhu>
          </div>
        </div>
      </The>

      {loi && <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400">{loi}</div>}

      <div className="flex flex-wrap gap-2">
        {(['nhom', 'bo-suu-tap', 'tham-gia'] as Tab[]).map(t => (
          <button key={t} onClick={() => setTab(t)} className={'rounded-xl px-4 py-2 text-sm font-bold ' + (tab === t ? 'bg-brand-600 text-white' : 'bg-white text-slate-600 shadow-card dark:bg-slate-800 dark:text-slate-300')}>
            {t === 'nhom' ? 'Nhóm đã đồng bộ' : t === 'bo-suu-tap' ? 'Bộ sưu tập' : 'Tìm & tham gia nhóm'}
          </button>
        ))}
      </div>

      {dangTai ? <The><DangTai dong={5} /></The> : tab === 'tham-gia' ? (
        <The className="overflow-hidden">
          <DauThe icon={<Send className="h-4 w-4" />} tieuDe="Tìm & tham gia nhóm" phai={<span className="text-xs font-semibold text-amber-600">Thí điểm: tối đa {JOIN_LIMIT} nhóm/lần</span>} />
          <div className="space-y-4 p-5">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
              Từ khóa hiện chỉ tìm trong nhóm đã đồng bộ. ID/link có thể gửi yêu cầu tham gia trực tiếp. Hệ thống chỉ báo đã tiếp nhận, không báo thành công giả.
            </div>
            <textarea value={joinInput} onChange={e => setJoinInput(e.target.value)} className="h-28 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-white" placeholder="Nhập ID, link nhóm hoặc từ khóa..." />
            <div className="flex flex-wrap gap-2">
              <NutChinh onClick={timUngVienJoin} disabled={!joinInput.trim()}><Search className="h-4 w-4" />Tìm/parse ứng viên</NutChinh>
              <NutPhu onClick={guiJoin} disabled={dangGuiJoin || joinSelected.size === 0}><Send className="h-4 w-4" />{dangGuiJoin ? 'Đang gửi...' : `Gửi yêu cầu (${joinSelected.size})`}</NutPhu>
            </div>
            {joinMsg && <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600 dark:bg-slate-900 dark:text-slate-300">{joinMsg}</div>}
            <GroupList groups={joinCandidates} selected={joinSelected} onToggle={id => setJoinSelected(s => toggleSet(s, id))} empty="Chưa có ứng viên tham gia nhóm." />
          </div>
        </The>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[280px_1fr]">
          <The className="overflow-hidden">
            <DauThe icon={<FolderKanban className="h-4 w-4" />} tieuDe="Bộ sưu tập" dem={collections.length} />
            <div className="space-y-2 p-4">
              <CollectionButton label="Tất cả nhóm" count={nhom.length} active={collectionDangChon === 'ALL'} onClick={() => setCollectionDangChon('ALL')} />
              <CollectionButton label="Chưa phân loại" count={nhom.filter(g => !g.collection_name).length} active={collectionDangChon === 'UNCLASSIFIED'} onClick={() => setCollectionDangChon('UNCLASSIFIED')} />
              {collections.map(c => <CollectionButton key={c.id || c.name} label={c.name} count={c.group_count || 0} active={collectionDangChon === c.name} onClick={() => setCollectionDangChon(c.name)} />)}
              <div className="flex gap-2 pt-3">
                <input value={tenBoMoi} onChange={e => setTenBoMoi(e.target.value)} className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-white" placeholder="Bộ mới" />
                <button onClick={taoBoSuuTap} className="rounded-lg bg-brand-600 px-3 text-white"><Plus className="h-4 w-4" /></button>
              </div>
            </div>
          </The>
          <The className="overflow-hidden">
            <DauThe icon={<Users className="h-4 w-4" />} tieuDe={tab === 'bo-suu-tap' ? 'Gán bộ sưu tập' : 'Nhóm đã đồng bộ'} dem={nhomLoc.length} />
            <div className="space-y-4 p-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                  <input value={tuKhoa} onChange={e => setTuKhoa(e.target.value)} className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-white" placeholder="Tìm theo tên hoặc ID nhóm" />
                </div>
                <select value={boGan} onChange={e => setBoGan(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-white">
                  <option value="">Chọn bộ sưu tập</option>
                  {collections.map(c => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
                </select>
                <NutChinh onClick={ganBoSuuTap} disabled={!boGan || daChon.size === 0}>Gán {daChon.size} nhóm</NutChinh>
              </div>
              <GroupList groups={nhomLoc} selected={daChon} onToggle={id => setDaChon(s => toggleSet(s, id))} empty="Chưa có nhóm nào trong bộ lọc này." />
            </div>
          </The>
        </div>
      )}
    </div>
  );
}

function toggleSet(source: Set<string>, id: string) {
  const next = new Set(source);
  if (next.has(id)) next.delete(id); else next.add(id);
  return next;
}

function CollectionButton({ label, count, active, onClick }: { label: string; count: number; active: boolean; onClick: () => void }) {
  return <button onClick={onClick} className={'flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold ' + (active ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300' : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/50')}><span className="truncate">{label}</span><span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs dark:bg-slate-700">{count}</span></button>;
}

function GroupList({ groups, selected, onToggle, empty }: { groups: Nhom[]; selected: Set<string>; onToggle: (id: string) => void; empty: string }) {
  if (groups.length === 0) return <ChuaCoDuLieu chu={empty} />;
  return <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 dark:divide-slate-700 dark:border-slate-700">{groups.map(g => <label key={g.id} className="flex cursor-pointer items-center gap-3 p-3 hover:bg-slate-50 dark:hover:bg-slate-700/40"><input type="checkbox" checked={selected.has(g.id)} onChange={() => onToggle(g.id)} className="h-4 w-4 rounded border-slate-300 text-brand-600" /><span className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-xs font-bold text-slate-500 dark:bg-slate-700">{g.avatar ? <img src={g.avatar} alt="" className="h-full w-full object-cover" /> : 'G'}</span><span className="min-w-0 flex-1"><span className="block truncate text-sm font-bold text-slate-900 dark:text-white">{g.name || `Nhóm ${g.id}`}</span><span className="block truncate text-xs text-slate-500 dark:text-slate-400">ID: {g.id} · {g.collection_name || 'Chưa phân loại'} {g.source ? `· ${g.source}` : ''}</span></span>{g.url && <a href={g.url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"><Link2 className="h-4 w-4" /></a>}</label>)}</div>;
}
